// A board that is not a puzzle: one to look at, walked through a line, or
// played on against the bot. It shows a position, marks the move that
// reached it, shows a hint, and — when it is given the legal moves — takes
// one of them. It knows no chess of its own beyond redrawing a move that
// moved two pieces; what is legal is the screen's to say.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Chess } from "chess.js";
import { BoardView, type Tone } from "./surface.ts";
import type { HintLevel } from "./puzzle.ts";
import { prefs } from "../app/prefs.ts";

const placement = (fen: string) => String(fen).split(" ").slice(0, 2).join(" ");

/** A reply drawn on the board, and what its buttons call it. */
export interface Reply {
  uci: string;
  label: string;
  /** Whose move it is; the board's own `tone` where it says nothing. */
  tone?: Tone;
}

/** The move that reached the position, or two moves from it: the one the
 *  line wants against the one that keeps being played. */
export type LastMove = string | { good?: string; bad?: string } | null;

export interface BoardInput {
  /** What may be played, in UCI. */
  legal: string[];
  onMove(uci: string): void;
}

export function Board({
  fen,
  orientation,
  last = null,
  hint = null,
  input = null,
  replies = [],
  tone = "reply",
  onReply,
  className,
}: {
  fen: string;
  orientation: "white" | "black";
  last?: LastMove;
  hint?: { level: HintLevel; moves: string[] } | null;
  input?: BoardInput | null;
  /** The moves the line offers from this position, drawn as arrows. */
  replies?: Reply[];
  /** Whose moves they are: your line's, or the replies prepared for. */
  tone?: Tone;
  /**
   * A reply chosen on the board. The square an arrow points to plays it; a
   * square several arrows point to asks which, right there. The piece an
   * arrow leaves from takes two taps: the first brings its arrows forward
   * and fades the rest, the second plays its reply — the first of them where
   * it has several, any of them from the square it points to.
   */
  onReply?: (uci: string) => void;
  className?: string;
}) {
  const el = useRef<HTMLDivElement>(null);
  const view = useRef<BoardView | null>(null);
  const inputRef = useRef(input);
  inputRef.current = input;
  const shown = useRef(fen);
  // The click that ends a move made by hand is not also a tap on an arrow.
  const justMoved = useRef(false);
  // Counts what changes the board. A position still being drawn when a move
  // is made by hand is overtaken by it, and must not put itself back when
  // its animation ends: the board would be showing the position before the
  // move, the screen the one after it, and the next move would go nowhere.
  const drawn = useRef(0);
  /** The latest way to mark the board, for a move that is still playing. */
  const marker = useRef<() => void>(() => {});

  // A pawn reaching the last rank queens: the one promotion anybody means in
  // an opening, and asking would put a dialog in the middle of a move.
  const legalMove = (from: string, to: string) => {
    const legal = inputRef.current?.legal || [];
    return legal.find((uci) => uci === from + to) || legal.find((uci) => uci === from + to + "q") || null;
  };

  useEffect(() => {
    if (!el.current) return;
    const board = new BoardView(el.current, {
      fen,
      orientation,
      style: prefs.board,
      pieces: prefs.pieces,
      onPick: (square) => {
        const can = (inputRef.current?.legal || []).some((uci) => uci.startsWith(square));
        if (can) board.mark("source", [square]);
        return can;
      },
      onDrop: (from, to) => {
        board.unmark("source");
        const uci = to ? legalMove(from, to) : null;
        if (!uci) return false;
        // Castling is two pieces and the hand moved one, a pawn on the last
        // rank is a queen now, and one taken in passing is not where the
        // hand went: the whole position is drawn at once, as part of the
        // same move, not when the screen's answer arrives.
        try {
          const game = new Chess(shown.current);
          const move = game.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci.slice(4, 5) || "q" });
          if (move) {
            // What the hand made is what the board shows: the screen's
            // answer that it is so draws nothing again. Drawn outright, it
            // also settles whatever was still being drawn, and a move that
            // moved two pieces or changed one.
            const after = game.fen();
            shown.current = after;
            drawn.current += 1;
            setTimeout(() => void board.settle(after), 0);
          }
        } catch {
          // A position chess.js cannot read: the screen's answer redraws it.
        }
        board.unmark();
        board.mark("last", [uci.slice(0, 2), uci.slice(2, 4)]);
        justMoved.current = true;
        setTimeout(() => inputRef.current?.onMove(uci), 0);
        return true;
      },
    });
    view.current = board;
    return () => {
      board.destroy();
      view.current = null;
    };
    // The board is made once; everything else is applied to it below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    view.current?.setOrientation(orientation);
  }, [orientation]);

  const lastKey = typeof last === "string" ? last : last ? `${last.good}|${last.bad}` : "";
  const ucis = replies.map((r) => r.uci);
  const toned = replies.map((r) => ({ uci: r.uci, tone: r.tone || tone }));
  const tonedKey = toned.map((r) => r.uci + r.tone).join(",");
  useEffect(() => {
    const board = view.current;
    if (!board) return;
    const mark = () => {
      board.unmark();
      const on = (kind: "last" | "wanted" | "played", uci?: string) => uci && uci.length >= 4 && board.mark(kind, [uci.slice(0, 2), uci.slice(2, 4)]);
      if (typeof last === "string") on("last", last);
      else if (last) {
        on("played", last.bad);
        on("wanted", last.good);
      }
      if (hint && hint.level !== "clear") board.hint(hint.level, hint.moves);
      board.replies(toned, null);
      // The squares that answer a tap, and look it: where the arrows point,
      // and the pieces they leave from.
      const targets = new Set(onReply ? ucis.flatMap((uci) => [uci.slice(0, 2), uci.slice(2, 4)]) : []);
      for (const square of el.current?.querySelectorAll<SVGElement>("rect.square[data-square]") || []) {
        square.classList.toggle("reply-target", targets.has(square.getAttribute("data-square") || ""));
      }
    };
    // The same pieces in the same places is the same picture, whatever the
    // two FENs say about en passant and the move counters.
    marker.current = mark;
    if (placement(shown.current) === placement(fen)) {
      shown.current = fen;
      mark();
      return;
    }
    shown.current = fen;
    const mine = ++drawn.current;
    // What is marked when the move has finished playing is what the screen
    // wants by then, not what it wanted when the move began: the book's
    // answer about the position — its arrows — often arrives while the
    // piece is still moving.
    const done = () => {
      if (mine === drawn.current) marker.current();
    };
    board.setPosition(fen, true).then(done, done);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fen, lastKey, hint?.level, hint?.moves.join(","), tonedKey]);

  useEffect(() => {
    view.current?.setInput(input && input.legal.length ? "free" : null);
  }, [input]);

  // A choice under way: a piece picked, whose arrows are in play, or a
  // square several arrows point to, asking which. Any new position ends it.
  const [picked, setPicked] = useState<{ kind: "piece" | "square"; square: string } | null>(null);
  useEffect(() => setPicked(null), [fen, tonedKey]);
  const inPlay = !picked ? null : ucis.filter((uci) => (picked.kind === "piece" ? uci.slice(0, 2) : uci.slice(2, 4)) === picked.square);
  useEffect(() => {
    view.current?.replies(toned, inPlay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked]);

  const tap = (event: React.MouseEvent) => {
    if (justMoved.current) {
      justMoved.current = false;
      setPicked(null);
      return;
    }
    if (!onReply || !replies.length) return;
    const square = (event.target as Element).closest("[data-square]")?.getAttribute("data-square");
    if (!square) return;
    const into = replies.filter((r) => r.uci.slice(2, 4) === square);
    const from = replies.filter((r) => r.uci.slice(0, 2) === square);
    // A piece picked: its arrows' squares play them.
    if (picked?.kind === "piece") {
      const mine = into.filter((r) => r.uci.slice(0, 2) === picked.square);
      if (mine.length) return onReply(mine[0]!.uci);
    }
    if (into.length === 1) return onReply(into[0]!.uci);
    if (into.length > 1) return setPicked({ kind: "square", square });
    // A piece with arrows, in two taps: the first brings its arrows forward
    // and fades the rest — and picks the piece up, where the board takes
    // moves by hand, since it may be going somewhere no arrow does — and the
    // second plays its reply, the first of them where it has several. The
    // square an arrow points to plays that one, whichever it is.
    if (picked?.kind === "piece" && picked.square === square && from.length) return onReply(from[0]!.uci);
    if (from.length === 1 && !inputRef.current) return onReply(from[0]!.uci);
    if (from.length) return setPicked({ kind: "piece", square });
    setPicked(null);
  };

  const wrap = useRef<HTMLDivElement>(null);
  return (
    <div className="board-wrap" ref={wrap}>
      <div className={"board" + (className ? " " + className : "")} ref={el} onClick={tap} />
      {picked?.kind === "square" && onReply ? (
        <ReplyChooser
          wrap={wrap}
          square={picked.square}
          replies={replies.filter((r) => r.uci.slice(2, 4) === picked.square)}
          shadeOf={(uci) => toned.filter((r) => r.tone === (replies.find((x) => x.uci === uci)?.tone || tone)).findIndex((r) => r.uci === uci)}
          toneOf={(uci) => replies.find((x) => x.uci === uci)?.tone || tone}
          onPick={onReply}
          onClose={() => setPicked(null)}
        />
      ) : null}
    </div>
  );
}

/** Which of the replies that end on one square: a button each, over the
 *  square, each in its arrow's shade. */
function ReplyChooser({
  wrap,
  square,
  replies,
  shadeOf,
  toneOf,
  onPick,
  onClose,
}: {
  wrap: React.RefObject<HTMLDivElement | null>;
  square: string;
  replies: Reply[];
  shadeOf: (uci: string) => number;
  toneOf: (uci: string) => Tone;
  onPick: (uci: string) => void;
  onClose: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const outer = wrap.current?.getBoundingClientRect();
    const cell = wrap.current?.querySelector(`rect.square[data-square="${square}"]`)?.getBoundingClientRect();
    const own = box.current?.getBoundingClientRect();
    if (!outer || !cell || !own) return;
    // Over the square, or under it when there is no room above; never off
    // the board's sides.
    const above = cell.top - outer.top - own.height - 6;
    const top = above >= 0 ? above : cell.bottom - outer.top + 6;
    const left = Math.min(Math.max(0, cell.left - outer.left + cell.width / 2 - own.width / 2), outer.width - own.width);
    setPlace({ left, top });
  }, [wrap, square]);
  useEffect(() => {
    const away = (event: PointerEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) onClose();
    };
    const key = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    // After the tap that opened it has finished.
    const timer = setTimeout(() => document.addEventListener("pointerdown", away), 0);
    document.addEventListener("keydown", key);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", key);
    };
  }, [onClose]);
  return (
    <div
      ref={box}
      className="reply-chooser"
      role="menu"
      aria-label={`Replies to ${square}`}
      style={place ? { left: place.left, top: place.top } : { visibility: "hidden" }}
    >
      {replies.map((r) => (
        <button key={r.uci} type="button" role="menuitem" className="reply-choice" onClick={() => onPick(r.uci)}>
          <span className={`reply-dot ${toneOf(r.uci)}-shade-${shadeOf(r.uci)}`} />
          {r.label}
        </button>
      ))}
    </div>
  );
}
